// ============================================================================
//  HOUSE BOT SERVICE - Client-side interface to server-side bot system

// ============================================================================
// All bot logic runs on the server. This service provides the client API
// to summon, dismiss, and check bot status.
// ============================================================================

import { doc, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';
import { API_BASE } from '../utils/apiBase';

const HOUSE_BOT_CONFIG = {
  ID_PREFIX: 'house_bot_',
};

// ============================================================================
// TYPES
// ============================================================================

export type BotDifficulty = 'easy' | 'medium' | 'hard';

export interface HouseBotMetadata {
  botId: string;
  botName: string;
  difficulty: BotDifficulty;
  buyInAmount: number;
  summonedBy: string;
  summonedAt: number;
  isLeaving: boolean;
  isKevinHart: boolean;
}

export interface SummonBotResult {
  success: boolean;
  bot?: {
    botId: string;
    botName: string;
    difficulty: BotDifficulty;
    buyInAmount: number;
  };
  error?: string;
}

export interface DismissBotResult {
  success: boolean;
  dismissed?: boolean;
  leaving?: boolean;
  error?: string;
}

// ============================================================================
// AUTH HELPER
// ============================================================================

async function getAuthToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function apiCall(endpoint: string, body: any): Promise<any> {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || `API error: ${response.status}`);
  }
  return data;
}

// ============================================================================
// PUBLIC API
// ============================================================================

export const houseBotService = {
  /**
   * Check if a player ID belongs to a house bot
   */
  isHouseBot(playerId: string): boolean {
    return playerId?.startsWith(HOUSE_BOT_CONFIG.ID_PREFIX) || false;
  },

  /**
   * Summon a house bot to a PlayCoin table
   * Bot is added server-side and begins playing automatically via Firestore trigger
   */
  async summonBot(
    tableId: string,
    difficulty: BotDifficulty = 'medium'
  ): Promise<SummonBotResult> {
    try {
      console.log(`🤖 [HouseBotService] Summoning ${difficulty} bot to table ${tableId}`);
      const result = await apiCall('/summonHouseBot', { tableId, difficulty });
      console.log(`🤖 [HouseBotService] Bot summoned:`, result.bot);
      return { success: true, bot: result.bot };
    } catch (error: any) {
      console.error('❌ [HouseBotService] Summon failed:', error);
      return { success: false, error: error.message || 'Failed to summon bot' };
    }
  },

  /**
   * Dismiss bot(s) from the table
   * If botId specified, dismiss that bot. Otherwise dismiss all.
   * If a hand is active, bot finishes the hand first (unless immediate=true)
   */
  async dismissBot(
    tableId: string,
    immediate: boolean = false,
    botId?: string
  ): Promise<DismissBotResult> {
    try {
      console.log(`🤖 [HouseBotService] Dismissing bot from table ${tableId} (immediate=${immediate})`);
      const result = await apiCall('/dismissHouseBot', { tableId, immediate, botId });
      console.log(`🤖 [HouseBotService] Dismiss result:`, result);
      return {
        success: true,
        dismissed: result.dismissed,
        leaving: result.leaving,
      };
    } catch (error: any) {
      console.error('❌ [HouseBotService] Dismiss failed:', error);
      return { success: false, error: error.message || 'Failed to dismiss bot' };
    }
  },

  /**
   * Get all bot metadata from table data (multi-bot support)
   * Returns array of bot metadata, with backward compat for old houseBotMetadata
   */
  getAllBots(tableData: any): HouseBotMetadata[] {
    if (tableData?.houseBots?.length > 0) return tableData.houseBots;
    if (tableData?.houseBotMetadata?.botId) {
      return [{ ...tableData.houseBotMetadata, isKevinHart: true }];
    }
    return [];
  },

  /**
   * Get single bot metadata (backward compat - returns first bot)
   */
  getBotMetadata(tableData: any): HouseBotMetadata | null {
    const bots = this.getAllBots(tableData);
    return bots.length > 0 ? bots[0] : null;
  },

  /**
   * Get bot count at table
   */
  getBotCount(tableData: any): number {
    return this.getAllBots(tableData).length;
  },

  /**
   * Check if a table has an active house bot (any non-leaving bot)
   */
  hasActiveBot(tableData: any): boolean {
    const bots = this.getAllBots(tableData);
    return bots.some(b => !!b.botId && !b.isLeaving);
  },

  /**
   * Subscribe to bot status changes on a table
   * Returns an unsubscribe function
   */
  onBotStatusChange(
    tableId: string,
    callback: (bots: HouseBotMetadata[]) => void
  ): () => void {
    const tableRef = doc(db, 'tables', tableId);
    return onSnapshot(tableRef, (snapshot) => {
      const data = snapshot.data();
      const bots = data?.houseBots || (data?.houseBotMetadata?.botId ? [{ ...data.houseBotMetadata, isKevinHart: true }] : []);
      callback(bots);
    });
  },
};

export default houseBotService;
